/**
 * Stripe Customer Service
 *
 * Creates orphan Stripe customers and links existing Stripe customers to the graph.
 * Optionally creates relationship instances from Stripe customer (STRIPE_CUSTOMER_ACCOUNT) to Client__c or Account__c.
 * Uses TransactionsApi when relating to Client/Account for atomic create+relate.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../gobodo/gobodoClient';
import { getCustomSuffix } from '../config/gobodoConfig';

const STRIPE_CUSTOMER_LINKED_TO_KEY = `stripe_customer_linked_to${getCustomSuffix()}`;

export interface StripeCustomerAccount {
  id: number;
  accountID: string;
  name: string;
  [key: string]: unknown;
}

export interface CreateOrphanStripeCustomerResult {
  account?: StripeCustomerAccount;
  failureException?: string;
}

export interface CreateAndLinkStripeCustomerResult {
  account?: StripeCustomerAccount;
  failureException?: string;
}

export interface CreateOrphanStripeCustomerInput {
  name: string;
  email?: string;
  metadata?: Record<string, string>;
  /** Graph object id of Client__c or Account__c to relate to (creates relationship after create) */
  clientIdOrAccountId?: string | number;
}

export interface CreateAndLinkStripeCustomerInput {
  existingCustomerId: string;
  refreshUrl?: string;
  returnUrl?: string;
  /** Graph object id of Client__c or Account__c to relate to (creates relationship after link) */
  clientIdOrAccountId?: string | number;
}

/**
 * Creates an orphan Stripe customer (not linked to any user).
 * Optionally creates a relationship from the new Stripe customer to the supplied Client or Account.
 * Uses a transaction when clientIdOrAccountId is provided so create+relate is atomic.
 */
export async function createOrphanStripeCustomer(
  input: CreateOrphanStripeCustomerInput
): Promise<CreateOrphanStripeCustomerResult> {
  const client = getGobodoClient();
  const stripeApi = new GobodoApi.StripeAccountsApi(client);
  const transactionsApi = new GobodoApi.TransactionsApi(client);
  const relationshipsApi = new GobodoApi.GraphRelationshipsApi(client);

  const useTransaction = input.clientIdOrAccountId != null;
  let txId: string | null = null;

  try {
    if (useTransaction) {
      const tx = await transactionsApi.beginTx();
      txId = tx?.id ?? tx?.getId?.();
      if (!txId) {
        throw new Error('Failed to start transaction');
      }
    }

    const createInput = new GobodoApi.CreateOrphanStripeCustomerInput();
    createInput.name = input.name;
    if (input.email) createInput.email = input.email;
    if (input.metadata) createInput.metadata = input.metadata;

    const opts: { transactionid?: string } = {};
    if (txId) opts.transactionid = txId;

    const data = await stripeApi.createOrphanStripeCustomer(createInput, opts);
    const account = data?.account ?? data?.getAccount?.();
    const failureException =
      typeof data?.failureException === 'string'
        ? data.failureException
        : data?.getFailureException?.();

    if (failureException) {
      if (txId) await transactionsApi.failTx(txId);
      return { failureException };
    }

    if (!account) {
      if (txId) await transactionsApi.failTx(txId);
      return { failureException: 'No account returned from createOrphanStripeCustomer' };
    }

    const stripeCustomerId = account.id ?? account.getId?.();
    const endId = input.clientIdOrAccountId != null ? Number(input.clientIdOrAccountId) : null;

    if (endId != null && !isNaN(endId) && txId) {
      const payload = new GobodoApi.GraphRelationshipPayload();
      payload.relationshiptypekey = STRIPE_CUSTOMER_LINKED_TO_KEY;
      payload.to = endId;

      try {
        await relationshipsApi.createRelationship(stripeCustomerId, payload, opts);
      } catch (relErr: unknown) {
        await transactionsApi.failTx(txId);
        const msg = relErr instanceof Error ? relErr.message : String(relErr);
        return { account: account as StripeCustomerAccount, failureException: `Create succeeded but relationship failed: ${msg}` };
      }
    } else if (endId != null && !isNaN(endId) && !txId) {
      const payload = new GobodoApi.GraphRelationshipPayload();
      payload.relationshiptypekey = STRIPE_CUSTOMER_LINKED_TO_KEY;
      payload.to = endId;
      try {
        await relationshipsApi.createRelationship(stripeCustomerId, payload, {});
      } catch (relErr: unknown) {
        const msg = relErr instanceof Error ? relErr.message : String(relErr);
        return { account: account as StripeCustomerAccount, failureException: `Create succeeded but relationship failed: ${msg}` };
      }
    }

    if (txId) await transactionsApi.commit(txId);

    return { account: account as StripeCustomerAccount };
  } catch (err: unknown) {
    if (txId) {
      try {
        await transactionsApi.failTx(txId);
      } catch (_) {
        // ignore
      }
    }
    const msg = err instanceof Error ? err.message : String(err);
    return { failureException: msg };
  }
}

/**
 * Links an existing Stripe customer (cus_xxx) to the graph.
 * Optionally creates a relationship from the linked Stripe customer to the supplied Client or Account.
 * Uses a transaction when clientIdOrAccountId is provided so link+relate is atomic.
 */
export async function createAndLinkStripeCustomerAccount(
  input: CreateAndLinkStripeCustomerInput
): Promise<CreateAndLinkStripeCustomerResult> {
  const client = getGobodoClient();
  const stripeApi = new GobodoApi.StripeAccountsApi(client);
  const transactionsApi = new GobodoApi.TransactionsApi(client);
  const relationshipsApi = new GobodoApi.GraphRelationshipsApi(client);

  const useTransaction = input.clientIdOrAccountId != null;
  let txId: string | null = null;

  try {
    if (useTransaction) {
      const tx = await transactionsApi.beginTx();
      txId = tx?.id ?? tx?.getId?.();
      if (!txId) {
        throw new Error('Failed to start transaction');
      }
    }

    const createInput = new GobodoApi.CreateStripeCustomerInput();
    createInput.existingCustomerId = input.existingCustomerId;
    if (input.refreshUrl) createInput.refreshUrl = input.refreshUrl;
    if (input.returnUrl) createInput.returnUrl = input.returnUrl;

    const opts: { transactionid?: string } = {};
    if (txId) opts.transactionid = txId;

    const data = await stripeApi.createAndLinkStripeCustomerAccount(createInput, opts);
    const account = data?.account ?? data?.getAccount?.();
    const failureException =
      typeof data?.failureException === 'string'
        ? data.failureException
        : data?.getFailureException?.();

    if (failureException) {
      if (txId) await transactionsApi.failTx(txId);
      return { failureException };
    }

    if (!account) {
      if (txId) await transactionsApi.failTx(txId);
      return { failureException: 'No account returned from createAndLinkStripeCustomerAccount' };
    }

    const stripeCustomerId = account.id ?? account.getId?.();
    const endId = input.clientIdOrAccountId != null ? Number(input.clientIdOrAccountId) : null;

    if (endId != null && !isNaN(endId) && txId) {
      const payload = new GobodoApi.GraphRelationshipPayload();
      payload.relationshiptypekey = STRIPE_CUSTOMER_LINKED_TO_KEY;
      payload.to = endId;

      try {
        await relationshipsApi.createRelationship(stripeCustomerId, payload, opts);
      } catch (relErr: unknown) {
        await transactionsApi.failTx(txId);
        const msg = relErr instanceof Error ? relErr.message : String(relErr);
        return { account: account as StripeCustomerAccount, failureException: `Link succeeded but relationship failed: ${msg}` };
      }
    } else if (endId != null && !isNaN(endId) && !txId) {
      const payload = new GobodoApi.GraphRelationshipPayload();
      payload.relationshiptypekey = STRIPE_CUSTOMER_LINKED_TO_KEY;
      payload.to = endId;
      try {
        await relationshipsApi.createRelationship(stripeCustomerId, payload, {});
      } catch (relErr: unknown) {
        const msg = relErr instanceof Error ? relErr.message : String(relErr);
        return { account: account as StripeCustomerAccount, failureException: `Link succeeded but relationship failed: ${msg}` };
      }
    }

    if (txId) await transactionsApi.commit(txId);

    return { account: account as StripeCustomerAccount };
  } catch (err: unknown) {
    if (txId) {
      try {
        await transactionsApi.failTx(txId);
      } catch (_) {
        // ignore
      }
    }
    const msg = err instanceof Error ? err.message : String(err);
    return { failureException: msg };
  }
}

/**
 * Gets the Stripe customer linked to a Client or Account (via incoming relationships of type stripe_customer_linked_to__c).
 * Returns the first linked Stripe customer if any.
 */
export async function getStripeCustomerForClientOrAccount(
  clientIdOrAccountId: number
): Promise<{ id: number; accountID?: string; name?: string } | null> {
  const client = getGobodoClient();
  const relTypesApi = new GobodoApi.GraphRelationshipTypesApi(client);
  const relsApi = new GobodoApi.GraphRelationshipsApi(client);

  let relType: { id: number };
  try {
    relType = await relTypesApi.getRelationshipTypeByKey(STRIPE_CUSTOMER_LINKED_TO_KEY);
  } catch {
    return null;
  }

  const relTypeId = relType?.id;
  if (relTypeId == null) return null;

  const rels = await relsApi.getRelationshipsByTypeIn(clientIdOrAccountId, relTypeId);
  const arr = Array.isArray(rels) ? rels : [];
  const first = arr[0];
  if (!first) return null;

  const startObj = first?.startobject ?? first?.startObject ?? first?.getStartobject?.();
  if (!startObj) return null;

  const id = startObj.id ?? startObj.getId?.();
  const accountID = startObj.accountID ?? startObj.accountId ?? startObj.getAccountID?.();
  const name = startObj.name ?? startObj.getName?.();

  return { id: Number(id), accountID, name };
}
